import { useMemo } from 'react';

import { buildBaselineSnapshot } from './launchChanges/buildBaselineSnapshot';
import { buildCurrentSnapshot } from './launchChanges/buildCurrentSnapshot';
import { diffLaunchConfigs } from './launchChanges/diffLaunchConfigs';
import { toLaunchChangeText } from './launchChanges/launchChangeText';

import type { EvaluationSummaryRow } from './components/LaunchSummary/LaunchSummary.types';
import type { LaunchChangeText } from './launchChanges/launchChangeText';
import type { useLaunchSummaryQueries } from './useLaunchSummaryQueries';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { useAmelioTranslations } from '@/hooks';

import { toCalendarLocale } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';

type LaunchSummaryQueriesResult = ReturnType<typeof useLaunchSummaryQueries>;

export interface UseLaunchChangesParams {
    group: LaunchSummaryQueriesResult['group'];
    orphanMilestones: LaunchSummaryQueriesResult['orphanMilestones'];
    checkedTypes: LaunchSummaryQueriesResult['checkedTypes'];
    cycleEvaluationByType: LaunchSummaryQueriesResult['cycleEvaluationByType'];
    evaluations: EvaluationSummaryRow[];
    locale: string;
    t: ReturnType<typeof useAmelioTranslations>['t'];
}

/**
 * Only the templates still assigned today can be named this way: a launched template since
 * replaced or deleted has no reachable source here, and falls back to `unknownTemplateLabel`.
 */
const toCurrentTemplateLabelById = (
    cycleEvaluationByType: LaunchSummaryQueriesResult['cycleEvaluationByType'],
): Map<string, string> =>
    new Map(
        Object.values(cycleEvaluationByType).flatMap((cycleEvaluation) => {
            const template = cycleEvaluation?.cycleQuestionnaire?.reviewTemplate;

            return template ? [[template.id, getLocalizedText(template.title)] as const] : [];
        }),
    );

export const useLaunchChanges = ({
    group,
    orphanMilestones,
    checkedTypes,
    cycleEvaluationByType,
    evaluations,
    locale,
    t,
}: UseLaunchChangesParams): LaunchChangeText[] | undefined =>
    useMemo(() => {
        const launchedConfiguration = group?.launchedConfiguration;

        if (!group || !launchedConfiguration) {
            return undefined;
        }

        const templateLabelByType: Partial<Record<EvaluationTypeValue, string>> =
            Object.fromEntries(
                evaluations.map((evaluation) => [evaluation.id, evaluation.templateLabel]),
            );

        const baseline = buildBaselineSnapshot({
            launchedConfiguration,
            templateLabelById: toCurrentTemplateLabelById(cycleEvaluationByType),
            unknownTemplateLabel: t('cycleEvaluationSchedule.launchSummary.evaluations.noTemplate'),
        });
        const current = buildCurrentSnapshot({
            group,
            orphanMilestones,
            checkedTypes,
            cycleEvaluationByType,
            templateLabelByType,
        });
        const calendarLocale = toCalendarLocale(locale);

        return diffLaunchConfigs(baseline, current).map((change) =>
            toLaunchChangeText({ change, calendarLocale, t }),
        );
    }, [group, orphanMilestones, checkedTypes, cycleEvaluationByType, evaluations, locale, t]);
